/**
 * Dropdown menu items reducer.
 * @module reducers/dropdownMenuNavItemsReducer
 */

import { GET_DROPDOWNMENU_NAVITEMS } from '../actions/index';
import {
  GET_BOWLINGCENTERPRODUCTSICON,
  GET_BOWLINGCENTERSEARCH,
  GET_CONTACTSFORMINFO,
  GET_QBASESNATIONLIST,
  SUBMIT_CONTACTSFORM,
} from '@redturtle/qubicaamf-theme/constants/ActionTypes';
import { getAppendedResultsQuery } from './getAppendedResultsQuery';

// Define types for the state and actions
interface DropdownMenuNavItemsState {
  error: string | null;
  hasError: boolean;
  result: any[];
  loadingResults: boolean;
}

interface DropdownMenuNavItemsAction {
  type: string;
  result?: any[];
  error?: string;
}

const initialState: DropdownMenuNavItemsState = {
  error: null,
  hasError: false,
  result: [],
  loadingResults: false,
};

export const dropdownMenuNavItemsReducer = (
  state = initialState,
  action: DropdownMenuNavItemsAction = { type: '' },
): DropdownMenuNavItemsState => {
  switch (action.type) {
    case `${GET_DROPDOWNMENU_NAVITEMS}_PENDING`:
      return {
        ...state,
        loadingResults: true,
      };
    case `${GET_DROPDOWNMENU_NAVITEMS}_SUCCESS`:
      return {
        ...state,
        result: action.result || [],
        loadingResults: false,
      };
    case `${GET_DROPDOWNMENU_NAVITEMS}_FAIL`:
      return {
        ...state,
        error: action.error || null,
        hasError: true,
        loadingResults: false,
      };
    default:
      return state;
  }
};
export type {};

// Define types for the state and actions
interface BowlingProductIconsInitialState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

interface BowlingProductIconsInitialAction {
  type: string;
  result?: any[];
  error?: string;
}

const productIconsInitialState: BowlingProductIconsInitialState = {
  loaded: false,
  loading: false,
  error: null,
};

export const bowlingcenterproductsicon = (
  state = productIconsInitialState,
  action: BowlingProductIconsInitialAction = { type: '' },
): BowlingProductIconsInitialState => {
  switch (action.type) {
    case `${GET_BOWLINGCENTERPRODUCTSICON}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${GET_BOWLINGCENTERPRODUCTSICON}_SUCCESS`:
      return {
        ...state,
        ...action.result,
        error: null,
        loaded: true,
        loading: false,
      };
    case `${GET_BOWLINGCENTERPRODUCTSICON}_FAIL`:
      return {
        ...state,
        error: action.error,
        loaded: false,
        loading: false,
      };
    default:
      return state;
  }
};

// Define types for the state and actions
interface BowlingCenterSearchInitialState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

interface BowlingCenterSearchInitialAction {
  type: string;
  result?: any[];
  error?: string;
}

const bowlingCenterSearchInitialState: BowlingCenterSearchInitialState = {
  loaded: false,
  loading: false,
  error: null,
};

export const bowlingcentersearch = (
  state = bowlingCenterSearchInitialState,
  action: BowlingCenterSearchInitialAction = { type: '' },
): BowlingCenterSearchInitialState => {
  switch (action.type) {
    case `${GET_BOWLINGCENTERSEARCH}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${GET_BOWLINGCENTERSEARCH}_SUCCESS`:
      return {
        ...state,
        ...action.result,
        error: null,
        loaded: true,
        loading: false,
      };
    case `${GET_BOWLINGCENTERSEARCH}_FAIL`:
      return {
        ...state,
        error: action.error,
        loaded: false,
        loading: false,
      };
    default:
      return state;
  }
};

export interface ContactFormInfoInitialState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
  data: {
    countries: { token: string; title: string }[];
    'new-investment-business-model-location': {
      token: string;
      title: string;
    }[];
    'new-investment-type-of-product': { token: string; title: string }[];
    products: { token: string; title: string }[];
  };
}

interface ContactFormInfoInitialAction {
  type: string;
  result?: {
    countries: { token: string; title: string }[];
    'new-investment-business-model-location': {
      token: string;
      title: string;
    }[];
    'new-investment-type-of-product': { token: string; title: string }[];
    products: { token: string; title: string }[];
  };
  error?: string;
}

const contactFormInfoInitialState: ContactFormInfoInitialState = {
  loaded: false,
  loading: false,
  error: null,
  data: {
    countries: [],
    'new-investment-business-model-location': [],
    'new-investment-type-of-product': [],
    products: [],
  },
};

export const contactFormInfo = (
  state = contactFormInfoInitialState,
  action: ContactFormInfoInitialAction = { type: '' },
): ContactFormInfoInitialState => {
  switch (action.type) {
    case `${GET_CONTACTSFORMINFO}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${GET_CONTACTSFORMINFO}_SUCCESS`:
      return {
        ...state,
        error: null,
        loaded: true,
        loading: false,
        data: action.result,
      };
    case `${GET_CONTACTSFORMINFO}_FAIL`:
      return {
        ...state,
        error: action.error,
        loaded: false,
        loading: false,
      };
    default:
      return state;
  }
};

export interface QBasesNationListInitialState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
  data: { id: string; loc_url: string; name: string }[];
}

interface qBasesNationListInitialAction {
  type: string;
  result?: { id: string; loc_url: string; name: string }[];
  error?: string;
}

const qBasesNationListInitialState: QBasesNationListInitialState = {
  loaded: false,
  loading: false,
  error: null,
  data: [],
};

export const qBasesNationList = (
  state = qBasesNationListInitialState,
  action: qBasesNationListInitialAction = { type: '' },
): QBasesNationListInitialState => {
  switch (action.type) {
    case `${GET_QBASESNATIONLIST}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${GET_QBASESNATIONLIST}_SUCCESS`:
      return {
        ...state,
        error: null,
        loaded: true,
        loading: false,
        data: action.result,
      };
    case `${GET_QBASESNATIONLIST}_FAIL`:
      return {
        ...state,
        error: action.error,
        loaded: false,
        loading: false,
      };
    default:
      return state;
  }
};

export interface SubmitContactFormInitialState {
  loaded: boolean;
  loading: boolean;
  error: string | object | null;
  result: any;
}

interface SubmitContactFormInitialAction {
  type: string;
  result?: any;
  error?: string;
}

const submitContactFormInitialState: SubmitContactFormInitialState = {
  loaded: false,
  loading: false,
  error: null,
  result: {},
};

export const submitContactForm = (
  state = submitContactFormInitialState,
  action: SubmitContactFormInitialAction = { type: '' },
): SubmitContactFormInitialState => {
  switch (action.type) {
    case `${SUBMIT_CONTACTSFORM}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${SUBMIT_CONTACTSFORM}_SUCCESS`:
      return {
        ...state,
        error: null,
        loaded: true,
        loading: false,
        result: action.result,
      };
    case `${SUBMIT_CONTACTSFORM}_FAIL`:
      return {
        ...state,
        error: action.error,
        loaded: false,
        loading: false,
      };
    case 'RESET_SUBMIT_CONTACT_FORM':
      return {
        ...submitContactFormInitialState,
      };
    default:
      return state;
  }
};

export { getAppendedResultsQuery };
